/**
 * Modelo puro do campo "Tipo de agenda" (SPEC 2.2 §5.5, mockup D): texto da
 * detecção, tipo efetivo e quadro de impacto. Sem Vue, para testar isolado.
 */
import type {
  AgendaDetectionResponse,
  ScheduleAgendaTypeValue
} from '@/Network/Types/Responses/DemandResponses'

export type AgendaTypeValue = ScheduleAgendaTypeValue
export type AgendaFieldMode = 'create' | 'edit'
export type EffectiveAgendaType = 'CONTINUOUS' | 'VARIABLE'

export interface TextSegment {
  text: string
  bold?: boolean
}

export interface DetectionText {
  variant: 'wait' | 'continuous' | 'moderate' | 'variable'
  segments: TextSegment[]
}

export interface ImpactModel {
  type: EffectiveAgendaType
  /** Aviso de que a escolha manual sobrescreve a detecção (só na edição). */
  overrideWarning: string | null
  /** AUTO + contínua com variação moderada: a Demanda mostra a faixa mín–máx. */
  moderateRange: boolean
}

// Limiares do spec: moderada 10–25 (inclusivo); baseline de 12 semanas.
const MODERATE_MIN = 10
const MODERATE_MAX = 25
const BASELINE_WEEKS = 12

export const AGENDA_TYPE_LABELS: Record<AgendaTypeValue, string> = {
  AUTO: 'Automático (padrão)',
  CONTINUOUS: 'Cobertura contínua',
  VARIABLE: 'Agenda variável'
}

function formatNumber(value: number, maxDecimals: number): string {
  return value.toLocaleString('pt-BR', { maximumFractionDigits: maxDecimals })
}

function isModerate(cv: number | null): boolean {
  return cv !== null && cv >= MODERATE_MIN && cv <= MODERATE_MAX
}

const WAIT_TEXT: DetectionText = {
  variant: 'wait',
  segments: [
    { text: '⏳ será detectado após 8 semanas de plantões — até lá entra em ' },
    { text: 'Cobertura contínua', bold: true }
  ]
}

function cvLabel(cv: number | null): string | null {
  return cv !== null ? `CV ${formatNumber(cv, 1)}%` : null
}

function detectedSegments(label: string, detail: string | null): TextSegment[] {
  return [
    { text: '🔎 ' },
    { text: `detectado: ${label}`, bold: true },
    ...(detail ? [{ text: ` · ${detail}` }] : [])
  ]
}

function variableDetail(cv: number | null, deletedPerLive: number | null): string | null {
  const parts: string[] = []
  const cvText = cvLabel(cv)
  if (cvText) parts.push(`${cvText} nas últimas ${BASELINE_WEEKS} semanas`)
  if (deletedPerLive !== null) {
    const noun = deletedPerLive >= 2 ? 'plantões apagados' : 'plantão apagado'
    parts.push(`${formatNumber(deletedPerLive, 2)} ${noun} por plantão vivo`)
  }
  return parts.length ? parts.join(' · ') : null
}

export function detectionText(
  detection: AgendaDetectionResponse | null,
  mode: AgendaFieldMode
): DetectionText {
  if (mode === 'create' || !detection?.detected) return WAIT_TEXT

  const { detected, cv, deletedPerLive } = detection
  if (detected === 'VARIABLE') {
    return {
      variant: 'variable',
      segments: detectedSegments(AGENDA_TYPE_LABELS.VARIABLE, variableDetail(cv, deletedPerLive))
    }
  }

  if (isModerate(cv)) {
    const detail = `variação moderada (${cvLabel(cv)} em ${BASELINE_WEEKS} semanas) — a Demanda mostra faixa mín–máx`
    return {
      variant: 'moderate',
      segments: detectedSegments(AGENDA_TYPE_LABELS.CONTINUOUS, detail)
    }
  }

  const cvText = cvLabel(cv)
  return {
    variant: 'continuous',
    segments: detectedSegments(
      AGENDA_TYPE_LABELS.CONTINUOUS,
      cvText ? `${cvText} nas últimas ${BASELINE_WEEKS} semanas` : null
    )
  }
}

export function detectionTextPlain(t: DetectionText): string {
  return t.segments.map((s) => s.text).join('')
}

/** Automático: detecção da escala; na criação ou sem histórico, contínua. */
export function effectiveType(
  choice: AgendaTypeValue,
  detection: AgendaDetectionResponse | null,
  mode: AgendaFieldMode
): EffectiveAgendaType {
  if (choice !== 'AUTO') return choice
  if (mode === 'create') return 'CONTINUOUS'
  return detection?.detected ?? 'CONTINUOUS'
}

function overrideWarning(
  choice: AgendaTypeValue,
  detected: EffectiveAgendaType | null,
  cv: number | null
): string | null {
  if (choice === 'CONTINUOUS' && detected === 'VARIABLE') {
    const detail = cv !== null ? ` · ${cvLabel(cv)}` : ''
    return `⚠ sobrescreve a detecção (Agenda variável${detail}): o alerta de queda pode gerar falsos positivos`
  }
  if (choice === 'VARIABLE' && detected === 'CONTINUOUS') {
    // O mockup cita a quantidade de quadros-meta; o endpoint de detecção não a
    // devolve, então o aviso fica genérico.
    return '⚠ Você está sobrescrevendo a detecção (Cobertura contínua). Os quadros-meta desta combinação deixam de medir déficit.'
  }
  return null
}

export function impactModel(
  choice: AgendaTypeValue,
  detection: AgendaDetectionResponse | null,
  mode: AgendaFieldMode
): ImpactModel {
  // Na criação não há detecção a sobrescrever.
  const detected = mode === 'edit' ? (detection?.detected ?? null) : null
  const cv = detection?.cv ?? null
  return {
    type: effectiveType(choice, detection, mode),
    overrideWarning: overrideWarning(choice, detected, cv),
    moderateRange: choice === 'AUTO' && detected === 'CONTINUOUS' && isModerate(cv)
  }
}
